import { useState } from 'react';
import { Popover } from 'antd';
import './notes.css';

// The 8 reaction keys are a contract with the DB and the admin portal
// (sale_note_reactions.reaction) — keep this list identical to
// cid_admin_portal/src/components/sales/comments/SaleCommentReactions.jsx.
export const REACTIONS = [
  { key: 'thumbs_up', emoji: '👍', label: 'Thumbs up' },
  { key: 'thumbs_down', emoji: '👎', label: 'Thumbs down' },
  { key: 'heart', emoji: '❤️', label: 'Heart' },
  { key: 'check', emoji: '✅', label: 'Check' },
  { key: 'eyes', emoji: '👀', label: 'Eyes' },
  { key: 'celebrate', emoji: '🎉', label: 'Celebrate' },
  { key: 'thinking', emoji: '🤔', label: 'Thinking' },
  { key: 'fire', emoji: '🔥', label: 'Fire' },
];

// The server sends { count, users, reacted } per key; tolerate the array / number
// shapes the admin component also accepts.
export const countOf = (v) => {
  if (!v) return 0;
  if (Array.isArray(v)) return v.length;
  if (typeof v === 'number') return v;
  return v.count || 0;
};
export const isReacted = (v) => (v && !Array.isArray(v) && typeof v === 'object' ? v.reacted === true : false);

// `showAdd` off renders only the pills that already exist — the conversation
// bubbles stay clean, and adding a reaction happens in the ··· sheet instead.
export default function NoteReactions({ reactions = {}, onToggle, showAdd = true }) {
  const [open, setOpen] = useState(false);

  const active = REACTIONS.filter((r) => countOf(reactions[r.key]) > 0);

  const toggle = (key) => {
    setOpen(false);
    onToggle?.(key, isReacted(reactions[key]));
  };

  const picker = (
    <div className="reaction-picker">
      {REACTIONS.map((r) => (
        <button
          key={r.key}
          type="button"
          title={r.label}
          className={isReacted(reactions[r.key]) ? 'reacted' : ''}
          onClick={() => toggle(r.key)}
        >
          {r.emoji}
        </button>
      ))}
    </div>
  );

  return (
    <div className="note-reactions">
      {active.map((r) => (
        <button
          key={r.key}
          type="button"
          title={(reactions[r.key]?.users || []).join(', ') || r.label}
          className={`reaction-badge ${isReacted(reactions[r.key]) ? 'reacted' : ''}`}
          onClick={() => toggle(r.key)}
        >
          <span>{r.emoji}</span>
          <span className="reaction-count">{countOf(reactions[r.key])}</span>
        </button>
      ))}

      {showAdd && (
      <Popover
        content={picker}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        // Below the button, not above: the reaction row sits at the BOTTOM of a
        // comment, so opening upward would cover the very text you're reacting
        // to. Downward it covers what comes after, which you aren't reading.
        placement="bottomLeft"
        arrow={false}
        // Without this antd portals to <body>, which is behind the notes sheet's
        // z-index — the picker opens but is invisible.
        getPopupContainer={(trigger) => trigger.parentNode}
      >
        <button type="button" className="reaction-add" aria-label="Add reaction">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" /><path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" /><line x1="9" y1="9.5" x2="9" y2="9.6" /><line x1="15" y1="9.5" x2="15" y2="9.6" />
          </svg>
        </button>
      </Popover>
      )}
    </div>
  );
}
